import type { ReduxAction, ID } from '../../types';
import { OrderedMap } from 'immutable';
import { AppState } from '@reducers/rootReducer';
import { Conversation } from '@models/nexus';

export interface NexusServiceStatus {
  onboarding_unavailable: boolean;
  is_processing_under_load: boolean;
}

type NexusProcessingStatus = 'ready' | 'processing' | 'error' | 'paywalled';
interface NexusUsage {
  snapshots: { total: number; used: number; fetching?: boolean; error?: boolean };
}

export interface NexusStatus {
  needs_permission: boolean;
  progress: number | null;
  snapshot_at: string | null;
  status: NexusProcessingStatus;
  usage: NexusUsage;
  loaded?: boolean;
}

export interface NexusState {
  conversations: OrderedMap<ID, Conversation>;
  serviceStatus: NexusServiceStatus;
  status: NexusStatus;
  enabling?: boolean;
  enablementFailed?: boolean;
  upgradeURL?: string;
}

const initialNexusState = {
  conversations: OrderedMap<ID, Conversation>(),
  serviceStatus: { onboarding_unavailable: false, is_processing_under_load: false },
  status: {
    needs_permission: false,
    progress: null,
    snapshot_at: null,
    status: null,
    usage: {
      snapshots: { total: 1, used: 0 },
    },
    loaded: false,
  },
};

export const selectNewConversation = (state: AppState): Conversation => {
  return state.data.nexus.conversations.get('new', new Conversation({}));
};

export const selectNewConversationID = (state: AppState): ID => {
  const newChatConversation = selectNewConversation(state);
  if (newChatConversation?.isPersisted) {
    return newChatConversation.id;
  }
  return null;
};

export const selectConversation =
  (conversationID: ID) =>
  (state: AppState): Conversation => {
    if (!conversationID) {
      return state.data.nexus.conversations.get('new', new Conversation({}));
    }
    return state.data.nexus.conversations.get(conversationID, new Conversation({}));
  };

export const selectAllConversations = (state: AppState): Conversation[] => {
  const newConversationID = selectNewConversationID(state);
  return state.data.nexus.conversations
    .filter((_, id) => id !== 'new' && id !== newConversationID)
    .valueSeq()
    .toArray();
};

export const selectUpgradeURL = (state: AppState): string => state.data.nexus.upgradeURL;
export const selectNexusServiceStatus = (state: AppState): NexusServiceStatus =>
  state.data.nexus.serviceStatus;
export const selectNexusStatus = (state: AppState): NexusStatus => state.data.nexus.status;
export const selectEnablingNexus = (state: AppState): boolean => state.data.nexus.enabling;
export const selectEnablingNexusFailed = (state: AppState): boolean =>
  state.data.nexus.enablementFailed;

export function nexusReducer(
  state: NexusState = initialNexusState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_NEXUS_SERVICE_STATUS_SUCCEEDED':
      return {
        ...state,
        serviceStatus: payload.response,
      };

    case 'FETCH_NEXUS_STATUS_SUCCEEDED':
      let status = payload.response.status;
      if (!['ready', 'error'].includes(status)) {
        status = 'processing';
      }
      return {
        ...state,
        status: {
          ...payload.response,
          status,
          loaded: true,
        },
      };

    case 'FETCH_NEXUS_STATUS_FAILED':
      // if this is a 403, set status to paywalled
      // console.log('payload', payload);
      // return state;
      return {
        ...state,
        status: {
          ...state.status,
          status: 'paywalled',
          loaded: true,
        },
        upgradeURL: payload.response?.upgrade_url,
      };

    case 'UPDATE_NEXUS_ENABLEMENT_REQUESTED':
      return {
        ...state,
        enabling: true,
        enablementFailed: false,
      };

    case 'UPDATE_NEXUS_ENABLEMENT_SUCCEEDED':
      if (payload?.updateUIOnSuccess) {
        return {
          ...state,
          enabling: false,
          conversations: state.conversations.set('new', new Conversation({})),
          status: {
            ...state.status,
            status: 'processing',
          },
        };
      } else {
        return {
          ...state,
          enabling: false,
        };
      }

    case 'UPDATE_NEXUS_ENABLEMENT_FAILED':
      return {
        ...state,
        enabling: false,
        enablementFailed: true,
      };

    case 'UPDATE_NEXUS_SNAPSHOT_REQUESTED':
      return {
        ...state,
        status: {
          ...state.status,
          usage: {
            ...state.status.usage,
            snapshots: {
              ...state.status.usage.snapshots,
              used: state.status.usage.snapshots.used + 1,
              fetching: true,
              error: false,
            },
          },
        },
      };

    case 'UPDATE_NEXUS_SNAPSHOT_SUCCEEDED':
      return {
        ...state,
        status: {
          ...state.status,
          status: 'processing',
          usage: {
            ...state.status.usage,
            snapshots: {
              ...state.status.usage.snapshots,
              fetching: false,
              error: false,
            },
          },
        },
      };

    case 'UPDATE_NEXUS_SNAPSHOT_FAILED':
      return {
        ...state,
        status: {
          ...state.status,
          usage: {
            ...state.status.usage,
            snapshots: {
              ...state.status.usage.snapshots,
              used: state.status.usage.snapshots.used - 1,
              fetching: false,
              error: true,
            },
          },
        },
      };

    case 'CLEAR_NEW_NEXUS_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.set('new', new Conversation({})),
      };

    case 'NEXUS_CONVERSATION_PAYWALLED':
      return {
        ...state,
        conversations: state.conversations.set(
          'new',
          new Conversation({ prompt: payload.prompt, paywalled: true }),
        ),
      };

    case 'NEXUS_CONVERSATION_NOT_ENABLED':
      return {
        ...state,
        conversations: state.conversations.set(
          'new',
          new Conversation({ prompt: payload.prompt, nexusDisabled: true }),
        ),
      };

    case 'INITIATE_NEXUS_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.set(
          'new',
          new Conversation({ prompt: payload.prompt, isFetching: true }),
        ),
      };

    case 'INITIATE_NEXUS_CONVERSATION_SUCCEEDED':
      // The UI can switch to consuming the new chat by ID once we've received the conversation ID
      const storedStartedChat = state.conversations
        .get('new')
        .copyWith({ id: payload.conversation_id });
      return {
        ...state,
        conversations: state.conversations
          .set('new', storedStartedChat)
          .set(payload.conversation_id, storedStartedChat),
      };

    case 'CONTINUE_NEXUS_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversationID, (chat) =>
          chat.addUserPrompt(payload.prompt),
        ),
      };

    case 'REGENERATE_NEXUS_CONVERSATION_RESPONSE':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversationID, (chat) =>
          chat.regenerateLastBotResponse(),
        ),
      };

    case 'NEXUS_CONVERSATION_MSG_CHUNK_RECEIVED':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversation_id, (chat) =>
          chat.buildBotResponse(payload),
        ),
      };

    case 'NEXUS_CONVERSATION_MSG_CONTACTS_RECEIVED':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversation_id, (chat) =>
          chat.setRelevantContactsForBotResponse(payload.message_id, payload.contacts),
        ),
      };

    case 'NEXUS_CONVERSATION_CONTACT_RECEIVED':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversation_id, (chat) =>
          chat.setTargetContactID(payload.contact_id),
        ),
      };

    case 'NEXUS_CONVERSATION_MSG_TERMINATED':
      return {
        ...state,
        conversations: state.conversations.update(payload.conversation_id, (chat) =>
          chat.terminateBotResponse(payload.message_id),
        ),
      };

    case 'NEXUS_CONVERSATION_MSG_ERROR':
      if (payload.conversation_id) {
        return {
          ...state,
          conversations: state.conversations.update(payload.conversation_id, (chat) =>
            chat.setBotResponseError(payload.message_id),
          ),
        };
      } else {
        return {
          ...state,
          conversations: state.conversations.update('new', (chat) => chat.setBotResponseError()),
        };
      }

    case 'DELETE_NEXUS_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.delete(payload.conversationID),
      };

    default:
      return state;
  }
}
